import React, {
    useState, useEffect, useCallback, useRef
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
    Text, Container, Grid, Button, Box, Divider
} from 'components/ui';
import Banner from 'components/Content/Banner';
import Copy from 'components/Content/Copy';
import Markdown from 'components/Markdown/Markdown';
import TextInput from 'components/Inputs/TextInput/TextInput';
import PromotionList from 'components/Content/PromotionList/PromotionList';
import urlUtils from 'utils/Url';
import languageLocaleUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import Actions from 'actions/Actions';
import advocacyApi from 'services/api/advocacy';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import rwdAdvocacyPageBindings from 'analytics/bindingMethods/pages/rwdAdvocacy/rwdAdvocacyPageBindings';
import PersonalizedPromotionsActions from 'actions/PersonalizedPromotionsActions';
import contentConstants from 'constants/content';
import { PAGE_TYPES } from 'components/Campaigns/Referrer/constants';
import { userSelector } from 'selectors/user/userSelector';
import { authSelector } from 'selectors/auth/authSelector';
import personalizedPromotionsSelector from 'selectors/personalizedPromotions/personalizedPromotionsSelector';
import appliedPromotionsSelector from 'selectors/basket/appliedPromotions/appliedPromotionsSelector';
import { wrapFunctionalComponent } from 'utils/framework';
import ReferrerShareLandingEN from 'components/Content/ReferrerShareLanding/locales/ReferrerShareLanding_en_US';
import ReferrerShareLandingFR from 'components/Content/ReferrerShareLanding/locales/ReferrerShareLanding_fr_CA';

const { getLocalisedDataFromResource } = languageLocaleUtils;
const getText = getLocalisedDataFromResource(ReferrerShareLandingEN, ReferrerShareLandingFR);
const { CONTEXTS } = contentConstants;

const SHARE_MMC = 'share-referral';
const COPIED_TEXT_TIMEOUT = 3000;
const CONTENT_MAX_WIDTH = ['100%', '50%'];
const LINK_FIELD_STYLE = { innerWrap: { borderWidth: 0, backgroundColor: 'transparent' }, input: { color: 'var(--color-gray)' } };

const buildShareUrl = referralLink => {
    const encodedFirstName = encodeURIComponent(userUtils.getProfileFirstName() || '');

    return `${referralLink}${referralLink.includes('?') ? '&' : '?'}om_mmc=${SHARE_MMC}&fn=${encodedFirstName}`;
};

const ReferralLinkField = ({ referralLink, copyUrl }) => {
    const [isCopied, setIsCopied] = useState(false);
    const copyTimeoutRef = useRef();

    useEffect(() => () => clearTimeout(copyTimeoutRef.current), []);

    const handleCopy = () => {
        setIsCopied(true);
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => setIsCopied(false), COPIED_TEXT_TIMEOUT);
    };

    return (
        <Grid
            columns='1fr auto'
            alignItems='center'
            gap={2}
            marginTop={5}
            paddingRight={3}
            border={1}
            borderColor='midGray'
            borderRadius={2}
            maxWidth={CONTENT_MAX_WIDTH}
        >
            <TextInput
                readOnly={true}
                marginBottom={null}
                value={referralLink}
                customStyle={LINK_FIELD_STYLE}
                data-at={Sephora.debug.dataAt('referrer_link')}
            />
            <CopyToClipboard
                text={copyUrl}
                onCopy={handleCopy}
            >
                <Button
                    variant='link'
                    data-at={Sephora.debug.dataAt('referrer_copy_btn')}
                    children={isCopied ? getText('copied') : getText('copy')}
                />
            </CopyToClipboard>
        </Grid>
    );
};

const ReferrerShareLandingContent = ({
    campaignCode, globalHeroBanner, promoHeadline, promoInstructions, referralDisclaimer
}) => {
    const dispatch = useDispatch();
    const user = useSelector(userSelector);
    const auth = useSelector(authSelector);
    const personalizedPromotions = useSelector(personalizedPromotionsSelector);
    const appliedPromotions = useSelector(appliedPromotionsSelector) || [];

    const isInitialized = user.isInitialized;
    const isAnonymous = userUtils.isAnonymous(auth);

    const [counter, setCounter] = useState({ data: null, loading: true, error: null });

    const campaign = counter.data;
    const limitReached = campaign?.tierRemaining === 0;
    const counterReady = !counter.loading && !counter.error;

    const fetchCampaigns = useCallback(async () => {
        setCounter({ data: null, loading: true, error: null });
        try {
            const campaigns = await advocacyApi.activeClientCampaigns({
                clientId: userUtils.getBiAccountId(),
                referrerFirstName: userUtils.getProfileFirstName(),
                referrerLastName: userUtils.getProfileLastName()
            });
            const match = Array.isArray(campaigns) ? campaigns.find(c => c.campaignCode === campaignCode) : null;
            setCounter({ data: match || null, loading: false, error: null });
        } catch (error) {
            setCounter({ data: null, loading: false, error: error?.errors?.[0]?.errorCode || true });
        }
    }, [campaignCode]);

    useEffect(() => {
        if (campaign) {
            rwdAdvocacyPageBindings.setPageLoadAnaytics(PAGE_TYPES.LANDING);
        }
    }, [campaign]);

    useEffect(() => {
        if (isInitialized && !isAnonymous) {
            fetchCampaigns();
        }
    }, [isInitialized, isAnonymous, fetchCampaigns]);

    useEffect(() => {
        if (limitReached) {
            dispatch(PersonalizedPromotionsActions.loadPersonalizedPromotions());
        }
    }, [limitReached, dispatch]);

    useEffect(() => {
        // Ineligible user / no matching campaign → redirect to the real 404 route so the server
        // returns a genuine HTTP 404 (standalone page), rather than rendering the error inline.
        if (counterReady && !campaign) {
            urlUtils.redirectTo(`${urlUtils.getLocalePathPrefix()}/error/404`);
        }
    }, [counterReady, campaign]);

    const handleShare = () => {
        const referralLink = campaign?.referralLink;

        if (!referralLink) {
            return;
        }

        const shareUrl = buildShareUrl(referralLink);

        processEvent.process(anaConsts.SHARE_EVENT, {
            data: { method: 'Share', contentType: 'advocacy referrer', itemId: campaign?.campaignCode }
        });

        if (navigator.share) {
            navigator
                .share({ title: getText('pageHeading'), url: shareUrl })
                .catch(error => Sephora.logger.error('[ReferrerShareLanding] Native share failed:', error));
        } else {
            dispatch(Actions.showShareLinkModal(true, getText('pageHeading'), shareUrl, getText('shareCta')));
        }
    };

    if (!isInitialized) {
        return null;
    }

    if (isAnonymous) {
        return (
            <Container
                marginY={[5, 7]}
                paddingX={[4, 5]}
            >
                <Text
                    is='h1'
                    fontWeight='bold'
                    fontSize={['lg', 'xl']}
                    children={getText('signInPrompt')}
                />
                <Button
                    variant='primary'
                    marginTop={4}
                    data-at={Sephora.debug.dataAt('referrer_sign_in_btn')}
                    onClick={() => dispatch(Actions.showSignInModal({ isOpen: true }))}
                    children={getText('signIn')}
                />
                <Button
                    variant='secondary'
                    marginTop={3}
                    data-at={Sephora.debug.dataAt('referrer_create_account_btn')}
                    onClick={() => dispatch(Actions.showRegisterModal({ isOpen: true }))}
                    children={getText('createAccount')}
                />
            </Container>
        );
    }

    if (counterReady && !campaign) {
        return null;
    }

    const heroBanner = globalHeroBanner?.[0];
    const hasBanner = Boolean(heroBanner?.media?.src);

    return (
        <Container
            marginY={[5, 7]}
            paddingX={[4, 5]}
        >
            <Grid
                columns={[1, hasBanner ? 2 : 1]}
                gap={[5, 6]}
                alignItems='start'
            >
                {hasBanner && (
                    <Banner
                        media={heroBanner.media}
                        largeMedia={heroBanner.largeMedia}
                        backgroundColor={heroBanner.backgroundColor}
                    />
                )}
                <Container>
                    <Markdown
                        inline
                        is='h1'
                        fontWeight='bold'
                        fontSize={['lg', 'xl']}
                        data-at={Sephora.debug.dataAt('referrer_heading')}
                        content={promoHeadline || getText('pageHeading')}
                    />

                    {counter.loading && (
                        <Text
                            is='p'
                            marginTop={3}
                            data-at={Sephora.debug.dataAt('referrer_counter_loading')}
                            children={getText('counterLoading')}
                        />
                    )}

                    {counter.error && (
                        <Box marginTop={3}>
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('referrer_counter_error')}
                                children={getText('counterError')}
                            />
                            <Button
                                variant='secondary'
                                marginTop={2}
                                data-at={Sephora.debug.dataAt('referrer_counter_retry')}
                                onClick={fetchCampaigns}
                                children={getText('retry')}
                            />
                        </Box>
                    )}

                    {counterReady && campaign && (
                        <Box
                            backgroundColor='lightBlue'
                            borderRadius={2}
                            padding={3}
                            marginTop={3}
                            maxWidth={CONTENT_MAX_WIDTH}
                            data-at={Sephora.debug.dataAt('referrer_counter')}
                        >
                            <Text is='span'>{'🎁 '}</Text>
                            <Text
                                is='span'
                                fontWeight='bold'
                                children={getText('counterCount', [campaign.tierUsageCount, campaign.tierLimit])}
                            />
                            <Text
                                is='span'
                                children={` ${getText('counterClaimedLabel')}`}
                            />
                        </Box>
                    )}

                    {promoInstructions && (
                        <Box
                            marginTop={5}
                            data-at={Sephora.debug.dataAt('referrer_instructions')}
                        >
                            <Copy
                                content={promoInstructions}
                                marginTop={0}
                                marginBottom={0}
                            />
                        </Box>
                    )}

                    {counterReady && campaign && (
                        limitReached ? (
                            <>
                                <Text
                                    is='h2'
                                    fontWeight='bold'
                                    marginTop={5}
                                    children={getText('limitReachedHeading')}
                                />
                                <Text
                                    is='p'
                                    marginTop={2}
                                    children={getText('limitReachedBody')}
                                />
                                <PromotionList
                                    context={CONTEXTS.CONTAINER}
                                    page='offers'
                                    items={personalizedPromotions.items}
                                    appliedPromotions={appliedPromotions}
                                    localization={{ viewAll: '' }}
                                    title={getText('offersTitle')}
                                />
                            </>
                        ) : (
                            <>
                                {campaign.referralLink && (
                                    <ReferralLinkField
                                        referralLink={campaign.referralLink}
                                        copyUrl={buildShareUrl(campaign.referralLink)}
                                    />
                                )}
                                <Button
                                    variant='primary'
                                    width={['100%', 280]}
                                    marginTop={4}
                                    data-at={Sephora.debug.dataAt('referrer_share_btn')}
                                    onClick={handleShare}
                                    children={getText('shareCta')}
                                />
                            </>
                        )
                    )}

                    {referralDisclaimer && (
                        <>
                            <Divider marginY={4} />
                            <Box
                                fontSize='sm'
                                data-at={Sephora.debug.dataAt('offer_disclaimer')}
                            >
                                <Copy
                                    content={referralDisclaimer}
                                    marginTop={0}
                                    marginBottom={0}
                                />
                            </Box>
                        </>
                    )}
                </Container>
            </Grid>
        </Container>
    );
};

const ReferrerShareLanding = props => {
    if (Sephora.configurationSettings.referrerLandingPageEnhancements?.isEnabled !== true) {
        return null;
    }

    return <ReferrerShareLandingContent {...props} />;
};

export default wrapFunctionalComponent(ReferrerShareLanding, 'ReferrerShareLanding');
